/**
 * Capability matrix used by the "AI you actually own" page
 * (`/platform/ai`). Renders the matrix from `AI_POSITIONING_PAGE.matrix`
 * — one column per competitor, one row per capability ClauseArk ships
 * today. Cells whose status we cannot back up with a public source
 * render the unverified glyph (`—`) per the page's policy footnote.
 *
 * Structurally a real table so screen readers announce headers + cells
 * correctly. Visually styled as a card with sticky-feeling header rows.
 */
import * as React from "react";
import { Check, Minus, X } from "lucide-react";
import {
  AI_POSITIONING_PAGE,
  type CapabilityMatrix as MatrixData,
  type MatrixCellStatus,
  type MatrixCompetitor,
} from "@/lib/content";

type LegendMap = Record<MatrixCellStatus, string>;

function CellGlyph({ status }: { status: MatrixCellStatus }) {
  if (status === "shipped") {
    return (
      <span
        className="inline-flex items-center justify-center text-emerald-600 dark:text-emerald-400"
        aria-label="Shipped"
        data-testid={`cell-glyph-shipped`}
      >
        <Check className="h-4 w-4" />
      </span>
    );
  }
  if (status === "partial") {
    return (
      <span
        className="inline-flex items-center justify-center text-amber-600 dark:text-amber-400"
        aria-label="Partial"
        data-testid={`cell-glyph-partial`}
      >
        <Minus className="h-4 w-4" />
      </span>
    );
  }
  if (status === "not-shipped") {
    return (
      <span
        className="inline-flex items-center justify-center text-rose-600 dark:text-rose-400"
        aria-label="Not standard"
        data-testid={`cell-glyph-not-shipped`}
      >
        <X className="h-4 w-4" />
      </span>
    );
  }
  // unverified
  return (
    <span
      className="inline-flex items-center justify-center text-slate-400 dark:text-slate-500 font-mono"
      aria-label="Unverified"
      data-testid={`cell-glyph-unverified`}
    >
      —
    </span>
  );
}

export interface CapabilityMatrixProps {
  matrix: MatrixData;
  legend?: LegendMap;
  unverifiedFootnote?: string;
  sourcesFootnote?: string;
}

/**
 * Renders the capability matrix and the two policy footnotes underneath.
 * `matrix` is intentionally a prop (not pulled from content directly)
 * so the same component can be reused by any page that wants to render
 * a comparison table — and so the test harness can pass synthetic data.
 */
export function CapabilityMatrix({
  matrix,
  legend = AI_POSITIONING_PAGE.matrix.legend,
  unverifiedFootnote = AI_POSITIONING_PAGE.matrix.unverifiedFootnote,
  sourcesFootnote = AI_POSITIONING_PAGE.matrix.sourcesFootnote,
}: CapabilityMatrixProps) {
  const competitors: ReadonlyArray<MatrixCompetitor> = matrix.competitors;
  return (
    <div data-testid="capability-matrix">
      <div className="overflow-x-auto rounded-2xl border border-mist dark:border-white/10 bg-white dark:bg-navy/40 shadow-sm">
        <table
          className="w-full min-w-[720px] border-collapse text-left text-sm"
          data-testid="capability-matrix-table"
        >
          <caption className="sr-only">
            ClauseArk capability matrix versus Ironclad, Spellbook, Evisort,
            DocuSign CLM, and LinkSquares. Cells render as Shipped, Partial,
            Not standard, or — (unverified).
          </caption>
          <thead>
            <tr className="bg-slate-50 dark:bg-navy/60">
              <th
                scope="col"
                className="sticky left-0 z-10 bg-slate-50 dark:bg-navy/60 px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy dark:text-white border-b border-mist dark:border-white/10"
              >
                Capability
              </th>
              <th
                scope="col"
                className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 border-b border-mist dark:border-white/10"
              >
                ClauseArk
              </th>
              {competitors.map((c) => (
                <th
                  key={c}
                  scope="col"
                  className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 border-b border-mist dark:border-white/10"
                  data-testid={`matrix-competitor-${c}`}
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrix.rows.map((row) => (
              <tr
                key={row.id}
                className="border-b border-mist/60 dark:border-white/5 last:border-b-0 hover:bg-slate-50/50 dark:hover:bg-navy/30"
                data-testid={`matrix-row-${row.id}`}
              >
                <th
                  scope="row"
                  className="sticky left-0 z-10 bg-white dark:bg-navy/40 px-4 py-4 align-top"
                >
                  <div className="font-semibold text-navy dark:text-white text-sm">
                    {row.title}
                  </div>
                  <div className="text-xs text-slate-600 dark:text-slate-400 mt-1 leading-relaxed max-w-xl">
                    {row.description}
                  </div>
                </th>
                <td
                  className="px-3 py-4 text-center align-middle bg-indigo-50/60 dark:bg-indigo-950/30"
                  data-testid={`matrix-cell-${row.id}-clauseark`}
                >
                  <CellGlyph status={row.ourStatus} />
                </td>
                {competitors.map((c) => (
                  <td
                    key={c}
                    className="px-3 py-4 text-center align-middle"
                    data-testid={`matrix-cell-${row.id}-${c}`}
                  >
                    <CellGlyph status={row.competitors[c]} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div
        className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-600 dark:text-slate-400"
        data-testid="capability-matrix-legend"
      >
        <span className="inline-flex items-center gap-2">
          <CellGlyph status="shipped" />
          <span>{legend.shipped}</span>
        </span>
        <span className="inline-flex items-center gap-2">
          <CellGlyph status="partial" />
          <span>{legend.partial}</span>
        </span>
        <span className="inline-flex items-center gap-2">
          <CellGlyph status="not-shipped" />
          <span>{legend["not-shipped"]}</span>
        </span>
        <span className="inline-flex items-center gap-2">
          <CellGlyph status="unverified" />
          <span>{legend.unverified}</span>
        </span>
      </div>

      <p
        className="mt-4 text-xs text-slate-500 dark:text-slate-400 leading-relaxed max-w-3xl"
        data-testid="capability-matrix-unverified-footnote"
      >
        <strong className="text-slate-700 dark:text-slate-300">
          About the “—” cells:
        </strong>{" "}
        {unverifiedFootnote}
      </p>
      <p
        className="mt-2 text-xs text-slate-500 dark:text-slate-400 leading-relaxed max-w-3xl"
        data-testid="capability-matrix-sources-footnote"
      >
        <strong className="text-slate-700 dark:text-slate-300">Sources:</strong>{" "}
        {sourcesFootnote}
      </p>
    </div>
  );
}
